import { BoothOtpMode, CibliError } from '@cibli/shared';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { UserInfo } from '@/features/register/context/register.context';

import { useAuth } from '@/features/auth/contexts/auth.context';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useDashboardUserSync } from '@/features/common/hooks/useDashboardUserSync';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { createInterview } from '@/services/interview';

import { useKioskOtp } from '../hooks/useKioskOtp';
import { RegisterContext, type RegisterContextValue } from './register-context.ref';

/** Session user shape when used to prefill register form (dashboard/session may expose these fields). */
type SessionUserRegisterFields = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  acceptedCGU?: boolean;
  cguId?: string;
};

function getErrorCode(error: unknown): string | undefined {
  if (error instanceof CibliError) {
    return error.response?.data?.code;
  }
  const maybeCode = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
  return typeof maybeCode === 'string' ? maybeCode : undefined;
}

function sessionUserToRegisterInitialData(user: SessionUserRegisterFields): Partial<UserInfo> {
  return {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || undefined,
    phone: user.phoneNumber || '',
    hasAcceptedCGU: user.acceptedCGU ?? false,
    cguId: user.cguId || '',
  };
}

type RegisterProviderProps = {
  children: ReactNode;
};

export function RegisterProvider({ children }: RegisterProviderProps) {
  const { t } = useTranslation();
  const { session, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { login } = useUserContext();
  const { createAndSetInterview, setInterview } = useInterviewContext();
  const { isDevOtpBypassEnabled, booth } = useBooth();
  const otpMode = booth?.otpMode ?? BoothOtpMode.SMS;
  const {
    otpState: kioskOtpState,
    isSendingOtp,
    startOtpFlow,
    handleOtpVerificationSuccess,
    handleOtpResend,
    handleEmailFallback,
    handleSmsFallback,
    handleClearSmsError,
    handleOtpBack,
  } = useKioskOtp({
    login,
    isDevOtpBypassEnabled,
    otpMode,
    machineUid: booth?.machineUid ?? undefined,
    onSkipOtpSuccess: async (userId: string) => {
      // First-time user: no session cookie exists (OTP was skipped).
      // createAndSetInterview relies on session → would fail.
      // Instead, create interview directly with the userId and navigate.
      const newInterview = await createInterview(userId);
      setInterview(newInterview);
      navigate('/interview', { replace: true });
    },
  });

  const { isRetrievingAuth } = useDashboardUserSync();

  // Only show loading when syncing auth from dashboard; no auto-redirect to /interview — user goes through home → language → onboarding, then tap sends to /interview if already auth
  const isAutoStartingWithSession = isRetrievingAuth;

  const submitRegister = useCallback(
    async (userInfos: UserInfo) => {
      try {
        await startOtpFlow(userInfos);
      } catch (error) {
        const errorCode = getErrorCode(error);
        const errorMessage = error instanceof Error ? error.message : '';
        const isArchivedError =
          errorCode === 'account_archived' ||
          errorCode === 'phone_archived' ||
          errorMessage.toLowerCase().includes('archiv');
        if (isArchivedError) {
          throw error;
        }
        throw error;
      }
    },
    [startOtpFlow],
  );

  const sessionRegisterInitialData = useMemo<Partial<UserInfo>>(() => {
    if (!session?.user) return {};
    return sessionUserToRegisterInitialData(session.user as SessionUserRegisterFields);
  }, [session?.user]);

  const [registerInitialData, setRegisterInitialData] = useState<Partial<UserInfo>>({});
  useEffect(() => {
    setRegisterInitialData(sessionRegisterInitialData);
  }, [sessionRegisterInitialData]);

  const [formError, setFormError] = useState<string | null>(null);

  const startInterview = useCallback(
    async (userInfos?: UserInfo) => {
      if (userInfos === undefined) {
        if (!isAuthenticated) return;
        const userId = session?.user?.id;
        if (!userId) {
          navigate('/register/cgu', { replace: true });
          return;
        }
        await createAndSetInterview(undefined, userId);
        navigate('/interview', { replace: true });
        return;
      }
      setRegisterInitialData(userInfos);
      setFormError(null);
      try {
        await submitRegister(userInfos);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '';
        const isArchived =
          errorMessage.toLowerCase().includes('archiv') ||
          (error as { response?: { data?: { code?: string } } })?.response?.data?.code ===
            'account_archived';
        if (isArchived) {
          setFormError(errorMessage || t('errors.account_archived'));
        }
        throw error;
      }
    },
    [isAuthenticated, session?.user?.id, createAndSetInterview, navigate, submitRegister, t],
  );

  const handleRegisterPhoneNext = useCallback(
    async (userInfos: UserInfo) => {
      await startInterview(userInfos);
    },
    [startInterview],
  );

  const value = useMemo<RegisterContextValue>(
    () => ({
      registerInitialData,
      setRegisterInitialData,
      formError,
      setFormError,
      isAutoStartingWithSession,
      isRegistering: isSendingOtp,
      startInterview,
      handleRegisterPhoneNext,
      kioskOtpState,
      handleOtpVerificationSuccess,
      handleOtpResend,
      handleEmailFallback,
      handleSmsFallback,
      handleClearSmsError,
      handleOtpBack,
    }),
    [
      registerInitialData,
      formError,
      isAutoStartingWithSession,
      isSendingOtp,
      startInterview,
      handleRegisterPhoneNext,
      kioskOtpState,
      handleOtpVerificationSuccess,
      handleOtpResend,
      handleEmailFallback,
      handleSmsFallback,
      handleClearSmsError,
      handleOtpBack,
    ],
  );

  return <RegisterContext.Provider value={value}>{children}</RegisterContext.Provider>;
}
